import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Body1,
  Button,
  Card,
  Checkbox,
  MessageBar,
  MessageBarBody,
  Spinner,
  Subtitle1,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import type { RequestDetail } from '@lg/core';
import { hasAnyRole } from '@lg/shared';
import { useApi } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { CoverageMatrixView } from '../people/CoverageMatrixView';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { errorMessage, shortDate } from '../ui/format';

const useStyles = makeStyles({
  stack: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  card: {
    padding: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  head: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  spacer: { flex: 1 },
});

/** Validation report and manual tasks (spec 11.4, 15.2). */
export function ReportTab({ detail }: { detail: RequestDetail }) {
  const styles = useStyles();
  const api = useApi();
  const qc = useQueryClient();
  const { user } = useAuth();
  const id = detail.request.id;
  const report = useQuery({ queryKey: ['report', id], queryFn: () => api.getReport(id) });
  const run = useQuery({ queryKey: ['run', id], queryFn: () => api.getLatestRun(id) });
  const isAdmin = hasAnyRole(user, ['D365Admin']);
  const [taskError, setTaskError] = useState<string | null>(null);

  if (report.isPending || run.isPending) return <Spinner label="Loading report…" />;
  if (report.isError)
    return (
      <MessageBar intent="error">
        <MessageBarBody>{errorMessage(report.error)}</MessageBarBody>
      </MessageBar>
    );
  const tasks = run.data?.tasks ?? [];
  const checksPass = !!report.data && report.data.checks.every((c) => c.status !== 'fail');
  const tasksDone = tasks.every((t) => t.status === 'Done');

  const toggle = async (taskId: string, done: boolean) => {
    setTaskError(null);
    try {
      await api.updateTask(id, taskId, { status: done ? 'Done' : 'Open' });
    } catch (err) {
      setTaskError(errorMessage(err));
    }
    await qc.invalidateQueries({ queryKey: ['run', id] });
    void qc.invalidateQueries({ queryKey: ['report', id] });
  };

  return (
    <div className={styles.stack}>
      <Card className={styles.card}>
        <div className={styles.head}>
          <Subtitle1 as="h2">Validation report</Subtitle1>
          {report.data && (
            <Badge color={checksPass ? 'success' : 'danger'}>
              {checksPass ? 'All checks pass' : 'Checks failing'}
            </Badge>
          )}
          <span className={styles.spacer} />
          {detail.request.status === 'Validating' && isAdmin && (
            <ConfirmDialog
              trigger={
                <Button appearance="primary" disabled={!checksPass || !tasksDone}>
                  Mark Ready
                </Button>
              }
              title={`Mark ${detail.request.legalEntityId ?? ''} ready to transact`}
              confirmLabel="Mark Ready"
              onConfirm={async () => {
                qc.setQueryData(['request', id], await api.completeRequest(id));
                void qc.invalidateQueries({ queryKey: ['requests'] });
              }}
            >
              <Body1>
                Every check passes and every manual task is done. The venue company is handed over as ready.
              </Body1>
            </ConfirmDialog>
          )}
          {detail.request.status === 'Ready' && <Badge color="success">Ready</Badge>}
        </div>
        {!report.data ? (
          <Body1>The report is built by the Validate step of the provisioning run.</Body1>
        ) : (
          <Table size="small">
            <TableHeader>
              <TableRow>
                <TableHeaderCell style={{ width: '22%' }}>Check</TableHeaderCell>
                <TableHeaderCell style={{ width: '12%' }}>Result</TableHeaderCell>
                <TableHeaderCell>Detail</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.data.checks.map((c) => (
                <TableRow key={c.key}>
                  <TableCell>
                    <b>{c.title}</b>
                  </TableCell>
                  <TableCell>
                    <Badge
                      appearance="tint"
                      color={c.status === 'pass' ? 'success' : c.status === 'fail' ? 'danger' : 'warning'}
                    >
                      {c.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{c.detail}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {report.data?.coverage && (
        <CoverageMatrixView coverage={report.data.coverage} onPick={() => undefined} />
      )}

      <Card className={styles.card}>
        <div className={styles.head}>
          <Subtitle1 as="h2">Manual tasks</Subtitle1>
          <Badge appearance="tint" color={tasksDone ? 'success' : 'warning'}>
            {tasks.filter((t) => t.status === 'Done').length} of {tasks.length} done
          </Badge>
        </div>
        <Body1>
          Out-of-scope items and steps behind feature flags become tracked tasks with an owner and due date.
        </Body1>
        {taskError && (
          <MessageBar intent="error" layout="multiline">
            <MessageBarBody>{taskError}</MessageBarBody>
          </MessageBar>
        )}
        <Table size="small">
          <TableHeader>
            <TableRow>
              <TableHeaderCell style={{ width: '8%' }}>Done</TableHeaderCell>
              <TableHeaderCell>Task</TableHeaderCell>
              <TableHeaderCell style={{ width: '24%' }}>Owner</TableHeaderCell>
              <TableHeaderCell style={{ width: '14%' }}>Due</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tasks.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>No manual tasks yet</TableCell>
              </TableRow>
            )}
            {tasks.map((t) => (
              <TableRow key={t.id}>
                <TableCell>
                  <Checkbox
                    checked={t.status === 'Done'}
                    disabled={!isAdmin || detail.request.status === 'Ready'}
                    aria-label={`Mark ${t.description} done`}
                    onChange={(_, d) => void toggle(t.id, d.checked === true)}
                  />
                </TableCell>
                <TableCell>{t.description}</TableCell>
                <TableCell>{t.ownerUpn ?? '—'}</TableCell>
                <TableCell>{shortDate(t.dueDate)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
